import { useId } from "react";
import realEstateImg from "@/assets/pharos/real_estate.jpg";
import privateEquityImg from "@/assets/pharos/private_equity.jpg";
import ventureImg from "@/assets/pharos/venture.jpg";
import creditImg from "@/assets/pharos/credit.jpg";
import infrastructureImg from "@/assets/pharos/infrastructure.jpg";
import otherImg from "@/assets/pharos/other.jpg";

// Pharos brand art. Palette is the Pharos/Elementar lockup (deliberately NOT
// `BRAND` from lib/constants.js, which is the Stemmer-Capital red used
// elsewhere in the app — Pharos is visually its own marketplace surface).
// Exported so any other Pharos component (e.g. PharosPage.jsx's ClassChip)
// can reference the same four hex values instead of re-hardcoding them out
// of sync.
export const INK = "#1a1a1a";
export const CREAM = "#f2ede4";
export const RED = "#e63329";
export const GRAY = "#8a8578";

// ---------------------------------------------------------------------------
// AssetBanner — real photography per asset class, full-bleed with a bottom-up
// dark scrim so on-image typography (the caller's `children`) stays legible.
// Source photos + licensing: src/assets/pharos/ATTRIBUTION.md.
// ---------------------------------------------------------------------------

const BANNER_IMAGES = {
  real_estate: realEstateImg,
  private_equity: privateEquityImg,
  venture: ventureImg,
  credit: creditImg,
  infrastructure: infrastructureImg,
  other: otherImg,
};

const BANNER_LABELS = {
  real_estate: "Real estate — glass skyscrapers",
  private_equity: "Private equity — waterfall and bridge",
  venture: "Venture — cosmic unicorn",
  credit: "Credit — gold bars",
  infrastructure: "Infrastructure — suspension bridge",
};

// Pharos v2 asset-class tokens -> the photo that represents them.
const CLASS_TO_IMAGE = {
  vc: "venture",
  pe: "private_equity",
  growth: "private_equity",
  re: "real_estate",
  credit: "credit",
  infra: "infrastructure",
};

const DEFAULT_BANNER_LABEL = "Pharos — coastal lighthouse";

// Full-bleed photo banner: the class photo, a bottom-up gradient scrim, then
// `children` on top of the scrim — this is how callers write editorial
// typography ON the picture (title, tag pills, key figures) instead of below
// it. `className` sizes the banner (e.g. "w-full h-44 rounded-t-xl") the same
// way it sized the old SVG root. Unknown/absent asset_class falls back to the
// lighthouse photo (the Pharos brand mark) so a browse card never renders
// blank. The venture photo carries its unicorn in the image itself
// (founder-provided art — see assets/pharos/ATTRIBUTION.md).
export function AssetBanner({ assetClass, className, children }) {
  const key = CLASS_TO_IMAGE[assetClass] || assetClass;
  const image = BANNER_IMAGES[key] || BANNER_IMAGES.other;
  const label = BANNER_LABELS[key] || DEFAULT_BANNER_LABEL;
  return (
    <div className={"relative overflow-hidden bg-black " + (className || "")}>
      <img src={image} alt={label} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
      {children && <div className="absolute inset-0 z-10">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// GlobePin — a small globe with a pin over the listing's region.
// ---------------------------------------------------------------------------

// Region labels mirror the REGIONS list in constants.js (kept local here so
// this file has no cross-file dependency — art.jsx is deliberately
// self-contained per its owning agent's file boundary).
const REGION_LABELS = {
  nordics: "Nordics",
  uk_ireland: "UK & Ireland",
  western_europe: "Western Europe",
  dach: "DACH",
  southern_europe: "Southern Europe",
  cee: "Central & Eastern Europe",
  israel: "Israel",
  mena: "MENA",
  north_america: "North America",
  latam: "Latin America",
  apac: "Asia-Pacific",
  global: "Global",
};

// Precomputed (x, y) in the globe's 0-100 viewBox, positioned over a
// stylized Atlantic-centered projection (Europe/Africa front-and-center,
// Americas left, Asia-Pacific at the right edge). "global" is intentionally
// absent here — it renders as a red equator ring instead of a pin.
const REGION_PIN = {
  nordics: { x: 54, y: 20 },
  uk_ireland: { x: 44, y: 24 },
  western_europe: { x: 50, y: 32 },
  dach: { x: 58, y: 34 },
  southern_europe: { x: 48, y: 44 },
  cee: { x: 62, y: 28 },
  israel: { x: 64, y: 46 },
  mena: { x: 54, y: 58 },
  north_america: { x: 24, y: 32 },
  latam: { x: 30, y: 66 },
  apac: { x: 80, y: 42 },
};

function Continents() {
  return (
    <g fill={INK}>
      <polygon points="8,20 26,14 34,22 30,34 20,42 10,36 4,26" />
      <polygon points="18,50 30,48 34,58 30,74 24,80 18,72 14,58" />
      <polygon points="44,10 62,14 68,26 60,34 66,44 56,54 60,68 48,80 38,64 34,40 40,22" />
      <polygon points="78,26 96,30 96,54 80,58 74,44 78,34" />
    </g>
  );
}

// Teardrop map-pin, tip at (x, y) — the precomputed location.
function pinPath(x, y) {
  const r = 6.5;
  const cy = y - r - 2;
  return `M${x},${y} L${x - r * 0.85},${cy + r * 0.3} A${r},${r} 0 1 1 ${x + r * 0.85},${cy + r * 0.3} Z`;
}

// Circular globe: outer ring, simplified continent silhouettes, a couple of
// subtle meridian/equator arcs, and a red pin at the region's location.
// "global" gets a red equator ring instead of a single pin; an unrecognized
// region renders the bare globe with no pin (never crashes).
export function GlobePin({ region, size = 44 }) {
  const uid = useId();
  const clipId = `pharos-globe-clip-${uid}`;
  const isGlobal = region === "global";
  const pin = REGION_PIN[region];
  const label = REGION_LABELS[region] || "Location";

  return (
    <svg width={size} height={size} viewBox="0 0 100 100" role="img" aria-label={label}>
      <title>{label}</title>
      <defs>
        <clipPath id={clipId}>
          <circle cx="50" cy="50" r="46" />
        </clipPath>
      </defs>
      <circle cx="50" cy="50" r="46" fill={CREAM} stroke={INK} strokeWidth="2.5" />
      <g clipPath={`url(#${clipId})`}>
        <Continents />
        <ellipse cx="50" cy="50" rx="46" ry="13" fill="none" stroke={GRAY} strokeWidth="1" opacity="0.55" />
        <ellipse cx="50" cy="50" rx="46" ry="28" fill="none" stroke={GRAY} strokeWidth="1" opacity="0.4" />
        <line x1="50" y1="4" x2="50" y2="96" stroke={GRAY} strokeWidth="1" opacity="0.35" />
      </g>
      <circle cx="50" cy="50" r="46" fill="none" stroke={INK} strokeWidth="2.5" />
      {isGlobal && <ellipse cx="50" cy="50" rx="46" ry="13" fill="none" stroke={RED} strokeWidth="2.5" />}
      {!isGlobal && pin && (
        <g>
          <path d={pinPath(pin.x, pin.y)} fill={RED} stroke={INK} strokeWidth="1" />
          <circle cx={pin.x} cy={pin.y - 8.5} r="2.2" fill={CREAM} />
        </g>
      )}
    </svg>
  );
}
